import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './ImageGallery.module.scss';
import Image from '../Image';
import useIsDesktop from '../../utils/useIsDesktop';
import i18n from '../../i18n';

const ImageGallery = ({ id, images }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(0);
    const imageCount = images.length;
    const isDesktop = useIsDesktop();
    const modalRef = useRef(null);
    const openModal = (imageId) => {
        setModalIsOpen(() => true);
        setCurrentImage(() => imageId);

        if (typeof window !== 'undefined') {
            document.body.classList.add('no-scroll');
        }
    };

    const closeModal = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (typeof window !== 'undefined') {
            document.body.classList.remove('no-scroll');
        }
        setModalIsOpen(() => false);
        setCurrentImage(() => 0);
    };

    const gotoPrevious = () => {
        setCurrentImage((currentImage) => {
            if (currentImage <= 0) {
                return imageCount - 1;
            } else {
                return currentImage - 1;
            }
        });
    };

    const gotoNext = () => {
        setCurrentImage((currentImage) => {
            if (currentImage >= imageCount - 1) {
                return 0;
            } else {
                return currentImage + 1;
            }
        });
    };

    const LEFT = 37;
    const RIGHT = 39;
    const ESC = 27;

    useEffect(() => {
        const cb = (e) => {
            if (e.keyCode === LEFT) {
                gotoPrevious();
            }
            if (e.keyCode === RIGHT) {
                gotoNext();
            }
            if (e.keyCode === ESC) {
                closeModal();
            }
        };
        window.addEventListener('keyup', cb);
        return () => window.removeEventListener('keyup', cb);
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */

    let originalTabIndexes = new Map();

    const handleFocusTrap = (event) => {
        const focusableElements = document.querySelectorAll('*');

        focusableElements.forEach((element) => {
            if (!modalRef.current.contains(element)) {
                originalTabIndexes.set(
                    element,
                    element.getAttribute('tabindex')
                );
                element.setAttribute('tabindex', '-1');
            }

            if (modalRef.current.contains(element)) {
                originalTabIndexes.set(
                    element,
                    element.getAttribute('tabindex')
                );
                if (element.getAttribute('tabindex') !== '-1') {
                    element.setAttribute('tabindex', '0');
                } else {
                    element.removeAttribute('tabindex');
                }
            }
        });

        modalRef.current.focus();
    };

    const resetTabIndexes = () => {
        originalTabIndexes.forEach((originalTabIndex, element) => {
            if (originalTabIndex !== null) {
                element.setAttribute('tabindex', originalTabIndex);
            } else {
                element.removeAttribute('tabindex');
            }
        });

        originalTabIndexes.clear();
    };

    useEffect(() => {
        if (modalIsOpen) {
            handleFocusTrap();
        }
        return () => {
            resetTabIndexes();
        };
    }, [modalIsOpen]);

    return (
        <div className={styles['ImageGallery']} id={id}>
            <div className={styles['ImageGallery__Content']}>
                {images.map((item, i) => {
                    if (i < 3) {
                        let showMore = false;
                        if (isDesktop && i === 1) showMore = true;
                        else if (!isDesktop && i === 2) showMore = true;
                        if (imageCount <= 3) showMore = false;

                        const igItem = {
                            id: `ImageGallery-${id}-item-${i}`,
                            galleryId: id,
                            showMore: showMore,
                            imageCount: images.length,
                            ...item,
                        };

                        return (
                            <ImageGalleryItem
                                imageId={i}
                                key={igItem.id}
                                {...igItem}
                                onClick={() => openModal(i)}
                            />
                        );
                    } else {
                        return null;
                    }
                })}
            </div>

            {modalIsOpen && (
                <div
                    className={styles['ImageGallery__Modal']}
                    ref={modalRef}
                    id={id}
                    tabIndex={-1}
                >
                    <button
                        onClick={(e) => closeModal(e)}
                        className={styles['ImageGallery__Modal__Close']}
                        aria-label={i18n.t('imageGallery.close')}
                    >
                        <span className="sr-only">
                            {i18n.t('imageGallery.close')}
                        </span>
                    </button>
                    <div className={styles['ImageGallery__Modal__Container']}>
                        {imageCount > 2 && (
                            <div
                                className={
                                    styles['ImageGallery__Modal__Navigation']
                                }
                                tabIndex={-1}
                            >
                                <button
                                    className={
                                        styles[
                                            'ImageGallery__Modal__Navigation--Prev'
                                        ]
                                    }
                                    tabIndex={0}
                                    onClick={gotoPrevious}
                                >
                                    <span
                                        className={
                                            styles[
                                                'ImageGallery__Modal__Navigation--Icon'
                                            ]
                                        }
                                        aria-hidden="true"
                                        tabIndex={-1}
                                    ></span>
                                    <span className="sr-only">
                                        {i18n.t('imageGallery.previous')}
                                    </span>
                                </button>
                                <button
                                    className={
                                        styles[
                                            'ImageGallery__Modal__Navigation--Next'
                                        ]
                                    }
                                    onClick={gotoNext}
                                    tabIndex={0}
                                >
                                    <span
                                        className={
                                            styles[
                                                'ImageGallery__Modal__Navigation--Icon'
                                            ]
                                        }
                                        aria-hidden="true"
                                        tabIndex={-1}
                                    ></span>
                                    <span className="sr-only">
                                        {i18n.t('imageGallery.next')}
                                    </span>
                                </button>
                            </div>
                        )}

                        {images.map((item, i) => {
                            const igItem = {
                                id: `ImageGalleryModal-${id}-item-${i}`,
                                galleryId: id,
                                ...item,
                            };

                            return (
                                <ImageGalleryModalItem
                                    key={igItem.id}
                                    imageId={i}
                                    currentImage={currentImage}
                                    imageCount={imageCount}
                                    {...item}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const ImageGalleryItem = ({
    image,
    galleryId,
    showMore,
    imageCount,
    onClick,
    imageId,
}) => {
    const cardClass = classNames(styles['ImageGallery__Item'], {
        [styles[`ImageGallery__Item--Darker`]]: showMore,
    });

    imageId = showMore ? 0 : imageId;

    return (
        <div className={cardClass} tabIndex={-1}>
            <button
                className={styles['ImageGallery__Item__Image']}
                aria-controls={galleryId}
                tabIndex={0}
                aria-label={i18n.t('imageGallery.zoom')}
                onClick={(e) => onClick(imageId)}
            >
                <div className={styles['ImageGallery__Item__Image__Overlay']} />
                {showMore && (
                    <span className={styles['ImageGallery__Item__ShowMore']}>
                        {i18n.t('imageGallery.showMore', imageCount)}
                    </span>
                )}
                <Image {...image} />
            </button>
        </div>
    );
};

const ImageGalleryModalItem = ({
    image,
    imageId,
    currentImage,
    imageCount,
}) => {
    const imageClass = classNames(styles['ImageGallery__Modal__Image'], {
        [styles['ImageGallery__Modal__Image--Active']]:
            currentImage === imageId,
    });

    if (currentImage !== imageId) return null;
    return (
        <div className={imageClass} tabIndex={-1}>
            <Image {...image} />
            <div
                className={styles['ImageGallery__Modal__Caption']}
                tabIndex={-1}
            >
                <h4>
                    {imageId + 1} / {imageCount}
                </h4>
                {image.imageCaption && <p tabIndex={0}>{image.imageCaption}</p>}
            </div>
        </div>
    );
};

ImageGallery.propTypes = {
    id: PropTypes.string.isRequired,
    images: PropTypes.array,
};

ImageGallery.defaultProps = {
    id: 'aaaa-bbbb-cccc-dddd',
    images: [],
};

ImageGalleryItem.propTypes = {
    image: PropTypes.object.isRequired,
    galleryId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    showMore: PropTypes.bool,
    imageCount: PropTypes.number.isRequired,
    item: PropTypes.object,
};

ImageGalleryItem.defaultProps = {
    image: [],
    galleryId: null,
    id: null,
    showMore: null,
    imageCount: 0,
    item: {},
};

ImageGalleryModalItem.propTypes = {
    text: PropTypes.string,
    image: PropTypes.object.isRequired,
    imageId: PropTypes.number.isRequired,
    currentImage: PropTypes.number.isRequired,
    imageCount: PropTypes.number.isRequired,
};

ImageGalleryModalItem.defaultProps = {
    text: '',
    image: [],
    imageId: 0,
    currentImage: 0,
    imageCount: 0,
};

export default ImageGallery;
